import React, { useState, useEffect } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/BulkOrder.css";

// ─── GraphQL ──────────────────────────────────────────────────────────────────
const GET_ALL_BULK_ENQUIRIES = gql`
  query GetAllBulkOrderEnquiries(
    $filter: BulkEnquiryFilterInput
    $page: Int
    $limit: Int
  ) {
    getAllBulkOrderEnquiries(filter: $filter, page: $page, limit: $limit) {
      success
      total
      page
      limit
      enquiries {
        id
        name
        email
        phone
        purpose
        productCategory
        quantityRequired
        budgetRange
        deliveryPincode
        companyName
        gstNumber
        referenceProductLink
        customizationRequired
        customizationDetails
        expectedDeliveryDate
        paymentMode
        preferredContactTime
        preferredContactMethod
        additionalNotes
        enquiryStatus
        assignedTo
        adminRemarks
        followUpDate
        convertedOrderId
        createdAt
        updatedAt
      }
    }
  }
`;

const UPDATE_BULK_ENQUIRY = gql`
  mutation UpdateBulkOrderEnquiry($id: ID!, $input: UpdateBulkOrderEnquiryInput!) {
    updateBulkOrderEnquiry(id: $id, input: $input) {
      success
      message
      enquiry {
        id
        enquiryStatus
        assignedTo
        adminRemarks
        followUpDate
        convertedOrderId
      }
    }
  }
`;

const DELETE_BULK_ENQUIRY = gql`
  mutation DeleteBulkOrderEnquiry($id: ID!) {
    deleteBulkOrderEnquiry(id: $id) {
      success
      message
    }
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  NEW:         { label: "New",         color: "#3B82F6", bg: "#EFF6FF" },
  CONTACTED:   { label: "Contacted",   color: "#F59E0B", bg: "#FFFBEB" },
  NEGOTIATING: { label: "Negotiating", color: "#8B5CF6", bg: "#F5F3FF" },
  CONVERTED:   { label: "Converted",   color: "#10B981", bg: "#ECFDF5" },
  CLOSED:      { label: "Closed",      color: "#6B7280", bg: "#F9FAFB" },
  REJECTED:    { label: "Rejected",    color: "#EF4444", bg: "#FEF2F2" },
};

const BUDGET_LABELS = {
  BELOW_50K:      "Below ₹50K",
  FROM_50K_TO_2L: "₹50K – ₹2L",
  FROM_2L_TO_10L: "₹2L – ₹10L",
  ABOVE_10L:      "Above ₹10L",
};

const PURPOSE_LABELS = {
  RESALE:            "Resale",
  PERSONAL_USE:      "Personal Use",
  CORPORATE_GIFTING: "Corporate Gifting",
  RESEARCH:          "Research",
  GOVERNMENT:        "Government",
  OTHER:             "Other",
};

const CATEGORY_LABELS = {
  DRONE:        "🚁 Drone",
  PARTS:        "⚙️ Parts",
  ACCESSORIES:  "🔌 Accessories",
  CUSTOM_BUILD: "🛠️ Custom Build",
};

const LIMIT = 10;

// ─── Component ────────────────────────────────────────────────────────────────
export default function BulkOrder() {
  const [page, setPage]               = useState(1);
  const [filter, setFilter]           = useState({});
  const [selectedId, setSelectedId]   = useState(null);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [toast, setToast]             = useState(null);
  const [searchTerm, setSearchTerm]   = useState("");

  // ─── Queries ───────────────────────────────────────────────────────────────
  const { data, loading, error, refetch } = useQuery(GET_ALL_BULK_ENQUIRIES, {
    variables: { filter, page, limit: LIMIT },
    fetchPolicy: "cache-and-network",
  });

  const [updateEnquiry, { loading: updating }] = useMutation(UPDATE_BULK_ENQUIRY);
  const [deleteEnquiry, { loading: deleting }] = useMutation(DELETE_BULK_ENQUIRY);

  const enquiries = data?.getAllBulkOrderEnquiries?.enquiries || [];
  const total     = data?.getAllBulkOrderEnquiries?.total || 0;
  const totalPages = Math.ceil(total / LIMIT);

  // ─── Filtered by search ────────────────────────────────────────────────────
  const filtered = enquiries.filter((e) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      e.name?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.phone?.includes(q) ||
      e.companyName?.toLowerCase().includes(q)
    );
  });

  // ─── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ─── Open Drawer ───────────────────────────────────────────────────────────
  const openDrawer = (enquiry) => {
    setSelectedId(enquiry.id);
    setEditForm({
      enquiryStatus:  enquiry.enquiryStatus  || "NEW",
      assignedTo:     enquiry.assignedTo     || "",
      adminRemarks:   enquiry.adminRemarks   || "",
      followUpDate:   enquiry.followUpDate   ? enquiry.followUpDate.slice(0, 10) : "",
      convertedOrderId: enquiry.convertedOrderId || "",
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedId(null);
  };

  // ─── Update ────────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    try {
      const input = {
        enquiryStatus: editForm.enquiryStatus,
        ...(editForm.assignedTo      && { assignedTo: editForm.assignedTo }),
        ...(editForm.adminRemarks    && { adminRemarks: editForm.adminRemarks }),
        ...(editForm.followUpDate    && { followUpDate: editForm.followUpDate }),
        ...(editForm.convertedOrderId && { convertedOrderId: editForm.convertedOrderId }),
      };

      const res = await updateEnquiry({ variables: { id: selectedId, input } });
      if (res.data?.updateBulkOrderEnquiry?.success) {
        showToast("Enquiry updated successfully");
        closeDrawer();
        refetch();
      } else {
        showToast(res.data?.updateBulkOrderEnquiry?.message || "Update failed", "error");
      }
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      const res = await deleteEnquiry({ variables: { id: deleteModal.id } });
      if (res.data?.deleteBulkOrderEnquiry?.success) {
        showToast("Enquiry deleted");
        setDeleteModal(null);
        refetch();
      } else {
        showToast("Delete failed", "error");
      }
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const selectedEnquiry = enquiries.find((e) => e.id === selectedId);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bo-root">

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={`bo-toast bo-toast--${toast.type}`}>
          <span>{toast.type === "success" ? "✅" : "❌"}</span>
          {toast.msg}
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="bo-header">
        <div className="bo-header__left">
          <h1 className="bo-title">Bulk Order Enquiries</h1>
          <span className="bo-badge">{total} total</span>
        </div>
        <div className="bo-header__right">
          <div className="bo-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              placeholder="Search name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="bo-filter-select"
            value={filter.enquiryStatus || ""}
            onChange={(e) => {
              setPage(1);
              setFilter(e.target.value ? { enquiryStatus: e.target.value } : {});
            }}
          >
            <option value="">All Status</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Stats Row ─────────────────────────────────────────────────────── */}
      <div className="bo-stats">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = enquiries.filter((e) => e.enquiryStatus === key).length;
          return (
            <div
              key={key}
              className="bo-stat-card"
              style={{ "--stat-color": cfg.color, "--stat-bg": cfg.bg }}
              onClick={() => {
                setPage(1);
                setFilter(filter.enquiryStatus === key ? {} : { enquiryStatus: key });
              }}
            >
              <span className="bo-stat-count">{count}</span>
              <span className="bo-stat-label">{cfg.label}</span>
            </div>
          );
        })}
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="bo-table-wrap">
        {loading ? (
          <div className="bo-empty">
            <div className="bo-spinner" />
            <p>Loading enquiries...</p>
          </div>
        ) : error ? (
          <div className="bo-empty bo-empty--error">
            <span>⚠️</span>
            <p>Failed to load: {error.message}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bo-empty">
            <span className="bo-empty__icon">📭</span>
            <p>No enquiries found</p>
          </div>
        ) : (
          <table className="bo-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Category</th>
                <th>Qty</th>
                <th>Budget</th>
                <th>Pincode</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((enq, idx) => {
                const status = STATUS_CONFIG[enq.enquiryStatus] || STATUS_CONFIG.NEW;
                const date   = enq.createdAt
                  ? new Date(Number(enq.createdAt)).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "—";

                return (
                  <tr key={enq.id} className="bo-row">
                    <td className="bo-row__num">{(page - 1) * LIMIT + idx + 1}</td>
                    <td>
                      <div className="bo-customer">
                        <div className="bo-avatar">
                          {enq.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="bo-customer__name">{enq.name}</div>
                          <div className="bo-customer__sub">{enq.phone}</div>
                          {enq.companyName && (
                            <div className="bo-customer__company">🏢 {enq.companyName}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="bo-chip bo-chip--cat">
                        {CATEGORY_LABELS[enq.productCategory] || enq.productCategory}
                      </span>
                      <div className="bo-purpose">
                        {PURPOSE_LABELS[enq.purpose] || enq.purpose}
                      </div>
                    </td>
                    <td className="bo-qty">{enq.quantityRequired}</td>
                    <td>
                      <span className="bo-chip bo-chip--budget">
                        {BUDGET_LABELS[enq.budgetRange] || enq.budgetRange}
                      </span>
                    </td>
                    <td className="bo-pincode">{enq.deliveryPincode}</td>
                    <td>
                      <span
                        className="bo-status"
                        style={{ color: status.color, background: status.bg }}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="bo-date">{date}</td>
                    <td>
                      <div className="bo-actions">
                        <button
                          className="bo-btn bo-btn--edit"
                          onClick={() => openDrawer(enq)}
                          title="Edit"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button
                          className="bo-btn bo-btn--delete"
                          onClick={() => setDeleteModal(enq)}
                          title="Delete"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="bo-pagination">
          <button
            className="bo-page-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>

          <div className="bo-page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "..." ? (
                  <span key={`dot-${i}`} className="bo-page-dot">…</span>
                ) : (
                  <button
                    key={p}
                    className={`bo-page-num ${p === page ? "active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
          </div>

          <button
            className="bo-page-btn"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>

          <span className="bo-page-info">
            {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total}
          </span>
        </div>
      )}

      {/* ── Edit Drawer ───────────────────────────────────────────────────── */}
      {drawerOpen && selectedEnquiry && (
        <>
          <div className="bo-overlay" onClick={closeDrawer} />
          <div className="bo-drawer">
            <div className="bo-drawer__header">
              <div>
                <h2 className="bo-drawer__title">Edit Enquiry</h2>
                <p className="bo-drawer__sub">{selectedEnquiry.name} • {selectedEnquiry.phone}</p>
              </div>
              <button className="bo-drawer__close" onClick={closeDrawer}>✕</button>
            </div>

            <div className="bo-drawer__body">

              {/* Enquiry Info (read-only) */}
              <div className="bo-drawer__section">
                <h3 className="bo-drawer__section-title">Enquiry Details</h3>
                <div className="bo-detail-grid">
                  <div className="bo-detail-item">
                    <span className="bo-detail-label">Email</span>
                    <span className="bo-detail-value">{selectedEnquiry.email}</span>
                  </div>
                  <div className="bo-detail-item">
                    <span className="bo-detail-label">Category</span>
                    <span className="bo-detail-value">{CATEGORY_LABELS[selectedEnquiry.productCategory]}</span>
                  </div>
                  <div className="bo-detail-item">
                    <span className="bo-detail-label">Quantity</span>
                    <span className="bo-detail-value">{selectedEnquiry.quantityRequired}</span>
                  </div>
                  <div className="bo-detail-item">
                    <span className="bo-detail-label">Budget</span>
                    <span className="bo-detail-value">{BUDGET_LABELS[selectedEnquiry.budgetRange]}</span>
                  </div>
                  <div className="bo-detail-item">
                    <span className="bo-detail-label">Purpose</span>
                    <span className="bo-detail-value">{PURPOSE_LABELS[selectedEnquiry.purpose]}</span>
                  </div>
                  <div className="bo-detail-item">
                    <span className="bo-detail-label">Pincode</span>
                    <span className="bo-detail-value">{selectedEnquiry.deliveryPincode}</span>
                  </div>
                  {selectedEnquiry.companyName && (
                    <div className="bo-detail-item">
                      <span className="bo-detail-label">Company</span>
                      <span className="bo-detail-value">{selectedEnquiry.companyName}</span>
                    </div>
                  )}
                  {selectedEnquiry.gstNumber && (
                    <div className="bo-detail-item">
                      <span className="bo-detail-label">GST</span>
                      <span className="bo-detail-value">{selectedEnquiry.gstNumber}</span>
                    </div>
                  )}
                  {selectedEnquiry.referenceProductLink && (
                    <div className="bo-detail-item bo-detail-item--full">
                      <span className="bo-detail-label">Ref Link</span>
                      <a href={selectedEnquiry.referenceProductLink} target="_blank" rel="noreferrer" className="bo-detail-link">
                        {selectedEnquiry.referenceProductLink}
                      </a>
                    </div>
                  )}
                  {selectedEnquiry.customizationRequired && (
                    <div className="bo-detail-item bo-detail-item--full">
                      <span className="bo-detail-label">Customization</span>
                      <span className="bo-detail-value">{selectedEnquiry.customizationDetails || "Yes"}</span>
                    </div>
                  )}
                  {selectedEnquiry.additionalNotes && (
                    <div className="bo-detail-item bo-detail-item--full">
                      <span className="bo-detail-label">Notes</span>
                      <span className="bo-detail-value">{selectedEnquiry.additionalNotes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* CRM Fields (editable) */}
              <div className="bo-drawer__section">
                <h3 className="bo-drawer__section-title">CRM Update</h3>

                <div className="bo-form-group">
                  <label>Status</label>
                  <select
                    value={editForm.enquiryStatus}
                    onChange={(e) => setEditForm({ ...editForm, enquiryStatus: e.target.value })}
                  >
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>

                <div className="bo-form-group">
                  <label>Assigned To</label>
                  <input
                    type="text"
                    placeholder="e.g. sales@flyhubs.in"
                    value={editForm.assignedTo}
                    onChange={(e) => setEditForm({ ...editForm, assignedTo: e.target.value })}
                  />
                </div>

                <div className="bo-form-group">
                  <label>Follow Up Date</label>
                  <input
                    type="date"
                    value={editForm.followUpDate}
                    onChange={(e) => setEditForm({ ...editForm, followUpDate: e.target.value })}
                  />
                </div>

                <div className="bo-form-group">
                  <label>Converted Order ID</label>
                  <input
                    type="text"
                    placeholder="Order ID if converted"
                    value={editForm.convertedOrderId}
                    onChange={(e) => setEditForm({ ...editForm, convertedOrderId: e.target.value })}
                  />
                </div>

                <div className="bo-form-group">
                  <label>Admin Remarks</label>
                  <textarea
                    rows={4}
                    placeholder="Internal notes about this enquiry..."
                    value={editForm.adminRemarks}
                    onChange={(e) => setEditForm({ ...editForm, adminRemarks: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="bo-drawer__footer">
              <button className="bo-drawer-btn bo-drawer-btn--cancel" onClick={closeDrawer}>
                Cancel
              </button>
              <button
                className="bo-drawer-btn bo-drawer-btn--save"
                onClick={handleUpdate}
                disabled={updating}
              >
                {updating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Delete Modal ──────────────────────────────────────────────────── */}
      {deleteModal && (
        <>
          <div className="bo-overlay" onClick={() => setDeleteModal(null)} />
          <div className="bo-modal">
            <div className="bo-modal__icon">🗑️</div>
            <h3 className="bo-modal__title">Delete Enquiry?</h3>
            <p className="bo-modal__desc">
              This will permanently delete the enquiry from{" "}
              <strong>{deleteModal.name}</strong>. This action cannot be undone.
            </p>
            <div className="bo-modal__actions">
              <button
                className="bo-drawer-btn bo-drawer-btn--cancel"
                onClick={() => setDeleteModal(null)}
              >
                Cancel
              </button>
              <button
                className="bo-drawer-btn bo-drawer-btn--delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}